import axios, { AxiosError } from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import {
  IEmailTemplate,
  CreateEmailTemplateRequest,
  UpdateEmailTemplateRequest,
  GetEmailTemplatesResponse,
  CheckLinkResult,
} from '../../types/emailTemplate/emailTemplate.types';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Create new email template
export const createEmailTemplate = async (
  templateData: CreateEmailTemplateRequest
): Promise<IEmailTemplate> => {
  try {
    const response = await api.post('/email-templates', templateData);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error creating email template:', error);
    throw error;
  }
};

// Get all email templates (optionally filtered by workspaceId)
export const getEmailTemplates = async (params?: {
  workspaceId?: string;
}): Promise<GetEmailTemplatesResponse> => {
  try {
    const response = await api.get(`/email-templates`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching email templates:', error);
    throw error;
  }
};

// Get email template by campaign ID
export const getEmailTemplateById = async (
  ID: string
): Promise<IEmailTemplate | null> => {
  try {
    const response = await api.get(`/email-templates/${ID}`);
    return response.data.data || response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      // No template found for this campaign
      return null;
    }
    console.error('Error fetching email template:', error);
    throw error;
  }
};

// Update email template
export const updateEmailTemplate = async (
  templateId: string,
  templateData: UpdateEmailTemplateRequest
): Promise<IEmailTemplate> => {
  try {
    const response = await api.put(
      `/email-templates/${templateId}`,
      templateData
    );
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error updating email template:', error);
    throw error;
  }
};

// Delete email template
export const deleteEmailTemplate = async (
  templateId: string
): Promise<void> => {
  try {
    await api.delete(`/email-templates/${templateId}`);
  } catch (error) {
    console.error('Error deleting email template:', error);
    throw error;
  }
};

// Check links (validate URLs from email template)
export const checkLinks = async (
  links: string[]
): Promise<CheckLinkResult[]> => {
  try {
    const response = await api.post<{
      success: boolean;
      data: CheckLinkResult[];
    }>('/email-templates/check-links', { links });
    return response.data?.data ?? response.data ?? [];
  } catch (error) {
    console.error('Error checking links:', error);
    throw error;
  }
};

export default api;
